import * as React from "react";
import {useTranslation} from "react-i18next";
import {useAuth} from "../context/AuthContext";
import {API_BASE_URL} from "../config/apiConfig";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface JobPosition {
    id: string;
    name: string;
    branch: string;
    active: boolean;
    status: string;
}

export interface ApprovedBranch {
    id: string;
    name: string;
}

export interface ApprovedJobOption {
    id: string;
    name: string;
    active: boolean;
    status: string;
}

// ─── Sorting helpers ──────────────────────────────────────────────────────────

export type Order = "asc" | "desc";
export type SortableKey = "name" | "status";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    const aVal = a[orderBy] ?? "";
    const bVal = b[orderBy] ?? "";
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export function getStatusColor(status: string): "success" | "error" | "default" {
    if (status?.toUpperCase() === "APPROVED") return "success";
    if (status?.toUpperCase() === "REJECTED") return "error";
    return "default";
}

export function getStatusChipSx(status: string): object {
    if (status?.toUpperCase() === "CLOSED") {
        return {borderColor: "#e91e63", color: "#e91e63"};
    }
    return {};
}

export function useJobPositions() {
    const {token} = useAuth();
    const {t} = useTranslation();

    // ── Shared data ─────────────────────────────────────────────────────────────
    const [jobs, setJobs] = React.useState<JobPosition[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    const [branches, setBranches] = React.useState<ApprovedBranch[]>([]);
    const [selectedBranch, setSelectedBranch] = React.useState<string>("All");

    // ── Original table state ────────────────────────────────────────────────────
    const [searchInput, setSearchInput] = React.useState<string>("");
    const [searchQuery, setSearchQuery] = React.useState<string>("");
    const [order, setOrder] = React.useState<Order>("asc");
    const [orderBy, setOrderBy] = React.useState<SortableKey>("name");
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const itemsPerPage = 10;

    // original table 3-dot menu
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [menuRowId, setMenuRowId] = React.useState<string | null>(null);
    const menuOpen = Boolean(anchorEl);

    // original table multi-select
    const [mainSelectedIds, setMainSelectedIds] = React.useState<string[]>([]);

    // ── Edit dialog (shared) ────────────────────────────────────────────────────
    const [editDialogOpen, setEditDialogOpen] = React.useState<boolean>(false);
    const [editRow, setEditRow] = React.useState<JobPosition | null>(null);
    const [editName, setEditName] = React.useState<string>("");
    const [editActive, setEditActive] = React.useState<boolean>(false);
    const [editFormErrors, setEditFormErrors] = React.useState<{ name?: string }>({});

    // ── Assign-edit dialog (bottom table) ───────────────────────────────────────
    const [assignEditOpen, setAssignEditOpen] = React.useState<boolean>(false);
    const [assignEditRow, setAssignEditRow] = React.useState<JobPosition | null>(null);
    const [assignEditJobId, setAssignEditJobId] = React.useState<string>("");
    const [assignEditActive, setAssignEditActive] = React.useState<boolean>(false);
    const [approvedJobOptions, setApprovedJobOptions] = React.useState<ApprovedJobOption[]>([]);
    const [approvedJobsLoading, setApprovedJobsLoading] = React.useState<boolean>(false);

    // ── Add dialog (original Add Job button) ────────────────────────────────────
    const [addDialogOpen, setAddDialogOpen] = React.useState<boolean>(false);
    const [addJobId, setAddJobId] = React.useState<string>("");
    const [addActive, setAddActive] = React.useState<boolean>(false);
    const [addBranchId, setAddBranchId] = React.useState<string>("");

    // ── New left card: inline add ───────────────────────────────────────────────
    const [inlineAddName, setInlineAddName] = React.useState<string>("");
    const [inlineAddActive, setInlineAddActive] = React.useState<boolean>(false);
    const [inlineAddSaving, setInlineAddSaving] = React.useState<boolean>(false);

    // ── New right card state ────────────────────────────────────────────────────
    const [allJobs, setAllJobs] = React.useState<JobPosition[]>([]);
    const [allJobsLoading, setAllJobsLoading] = React.useState<boolean>(false);
    const [cardSearch, setCardSearch] = React.useState<string>("");
    const [cardSearchQuery, setCardSearchQuery] = React.useState<string>("");
    const [cardOrder, setCardOrder] = React.useState<Order>("asc");
    const [cardOrderBy, setCardOrderBy] = React.useState<SortableKey>("name");
    const [cardPage, setCardPage] = React.useState<number>(1);
    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
    const [statusFilter, setStatusFilter] = React.useState<"Active" | "Inactive" | "All">("Active");
    const [selectedPositionId, setSelectedPositionId] = React.useState<string | null>(null);

    // new right card 3-dot menu (separate from original to avoid conflicts)
    const [cardAnchorEl, setCardAnchorEl] = React.useState<null | HTMLElement>(null);
    const [cardMenuRowId, setCardMenuRowId] = React.useState<string | null>(null);
    const cardMenuOpen = Boolean(cardAnchorEl);

    // ── Snackbar ────────────────────────────────────────────────────────────────
    const [snackbarOpen, setSnackbarOpen] = React.useState<boolean>(false);
    const [snackbarText, setSnackbarText] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<"success" | "error">("success");

    const showSnackbar = (text: string, severity: "success" | "error" = "success") => {
        setSnackbarText(text);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    // ─── Data fetch ───────────────────────────────────────────────────────────

    const fetchJobs = React.useCallback(async (branchId?: string) => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const url =
                branchId && branchId !== "All"
                    ? `${API_BASE_URL}/api/job-positions?branchId=${encodeURIComponent(branchId)}`
                    : `${API_BASE_URL}/api/job-positions`;
            const response = await fetch(url, {
                method: "GET",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
            });
            if (!response.ok) throw new Error(`Failed to fetch job positions: ${response.statusText}`);
            const data = await response.json();
            setJobs(data.jobs ?? data ?? []);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.fetchFailed'));
            setJobs([]);
        } finally {
            setLoading(false);
        }
    }, [token, t]);

    const fetchBranches = React.useCallback(async () => {
        if (!token) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/branches/get?status=APPROVED`, {
                method: "GET",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
            });
            if (!response.ok) return;
            const data = await response.json();
            setBranches(data.tenants ?? []);
        } catch {
            setBranches([]);
        }
    }, [token]);

    const fetchAllJobs = React.useCallback(async () => {
        if (!token) return;
        setAllJobsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/job-positions/all`, {
                method: "GET",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
            });
            if (!response.ok) throw new Error(`Failed to fetch all job positions: ${response.statusText}`);
            const data = await response.json();
            setAllJobs(data.jobs ?? []);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.fetchAllFailed'));
            setAllJobs([]);
        } finally {
            setAllJobsLoading(false);
        }
    }, [token, t]);

    React.useEffect(() => {
        fetchJobs();
        fetchBranches();
        fetchAllJobs();
    }, [fetchJobs, fetchBranches, fetchAllJobs]);

    const handleBranchChange = (value: string) => {
        setSelectedBranch(value);
        fetchJobs(value);
    };

    // ─── Original table: sort / filter / pagination ───────────────────────────

    const handleRequestSort = (field: SortableKey) => {
        const isAsc = orderBy === field && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(field);
    };

    const filteredJobs = React.useMemo(() => {
        if (!searchQuery.trim()) return jobs;
        const q = searchQuery.toLowerCase();
        return jobs.filter(
            (j) =>
                j.name?.toLowerCase().includes(q) ||
                j.status?.toLowerCase().includes(q) ||
                j.branch?.toLowerCase().includes(q),
        );
    }, [jobs, searchQuery]);

    const sortedJobs = React.useMemo(
        () => [...filteredJobs].sort(getComparator(order, orderBy)),
        [filteredJobs, order, orderBy],
    );

    const paginatedJobs = React.useMemo(
        () => sortedJobs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
        [sortedJobs, currentPage],
    );

    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedBranch]);

    // ─── Original table: 3-dot menu ──────────────────────────────────────────

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
        setAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRowId(null);
    };
    const handleMenuAction = (action: () => void) => {
        handleMenuClose();
        action();
    };

    // ─── Original table: multi-select helpers ────────────────────────────────

    // A row in the assignment table cannot be approved if its parent job (matched
    // by name in allJobs) has status REJECTED.
    const isParentRejected = React.useCallback(
        (row: JobPosition): boolean => {
            const s = allJobs.find((j) => j.name === row.name)?.status?.toUpperCase();
            return s === "REJECTED" || s === "CLOSED";
        },
        [allJobs],
    );

    const mainAllPageSelected =
        paginatedJobs.length > 0 &&
        paginatedJobs
            .filter((j) => !isParentRejected(j))
            .every((j) => mainSelectedIds.includes(j.id));
    const mainSomePageSelected =
        paginatedJobs.some((j) => mainSelectedIds.includes(j.id)) && !mainAllPageSelected;

    const handleMainSelectAll = (checked: boolean) =>
        setMainSelectedIds(
            checked ? paginatedJobs.filter((j) => !isParentRejected(j)).map((j) => j.id) : [],
        );
    const handleMainSelectRow = (id: string, checked: boolean) =>
        setMainSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((sid) => sid !== id)));

    // ─── Original table: child-branch approve / reject ───────────────────────

    const postChildStatusUpdates = async (
        updates: { id: string; status: string }[],
    ): Promise<void> => {
        const response = await fetch(
            `${API_BASE_URL}/api/job-positions/status/update/child-branch`,
            {
                method: "PUT",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
                body: JSON.stringify({statusUpdates: updates}),
            },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
    };

    const handleMainApprove = async (id: string) => {
        try {
            await postChildStatusUpdates([{id, status: "APPROVED"}]);
            showSnackbar(t('hr.jobs.success.assignmentApproved'));
            await fetchJobs(selectedBranch);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.assignmentApproveFailed'));
        }
    };

    const handleMainReject = async (id: string) => {
        try {
            await postChildStatusUpdates([{id, status: "REJECTED"}]);
            showSnackbar(t('hr.jobs.success.assignmentRejected'));
            await fetchJobs(selectedBranch);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.assignmentRejectFailed'));
        }
    };

    const handleMainBulkApprove = async () => {
        const ids = [...mainSelectedIds];
        try {
            await postChildStatusUpdates(ids.map((id) => ({id, status: "APPROVED"})));
            setMainSelectedIds([]);
            showSnackbar(t('hr.jobs.success.assignmentsApproved', {count: ids.length}));
            await fetchJobs(selectedBranch);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.assignmentsApproveFailed'));
        }
    };

    const handleMainBulkReject = async () => {
        const ids = [...mainSelectedIds];
        try {
            await postChildStatusUpdates(ids.map((id) => ({id, status: "REJECTED"})));
            setMainSelectedIds([]);
            showSnackbar(t('hr.jobs.success.assignmentsRejected', {count: ids.length}));
            await fetchJobs(selectedBranch);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.assignmentsRejectFailed'));
        }
    };

    const handleMainClose = async (id: string) => {
        try {
            await postChildStatusUpdates([{id, status: "CLOSED"}]);
            showSnackbar(t('hr.jobs.success.assignmentClosed'));
            await fetchJobs(selectedBranch);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.assignmentCloseFailed'));
        }
    };

    const handleMainBulkClose = async () => {
        const ids = [...mainSelectedIds];
        try {
            await postChildStatusUpdates(ids.map((id) => ({id, status: "CLOSED"})));
            setMainSelectedIds([]);
            showSnackbar(t('hr.jobs.success.assignmentsClosed', {count: ids.length}));
            await fetchJobs(selectedBranch);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.assignmentsCloseFailed'));
        }
    };

    // ─── Edit dialog ──────────────────────────────────────────────────────────

    const handleEditOpen = (row: JobPosition) => {
        setEditRow(row);
        setEditName(row.name);
        setEditActive(row.active);
        setEditFormErrors({});
        setEditDialogOpen(true);
    };
    const handleEditClose = () => {
        setEditDialogOpen(false);
        setEditRow(null);
    };

    const handleEditSave = async () => {
        if (!editName.trim()) {
            setEditFormErrors({name: t('hr.branches.validation.nameRequired')});
            return;
        }
        if (!editRow) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/job-positions/update`, {
                method: "PUT",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
                body: JSON.stringify({
                    id: editRow.id,
                    branchId: editRow.branch,
                    name: editName,
                    active: editActive === true
                }),
            });
            if (!response.ok) throw new Error(`Failed to update job position: ${response.statusText}`);
            handleEditClose();
            showSnackbar(t('hr.jobs.success.positionUpdated'));
            await fetchJobs(selectedBranch);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.positionUpdateFailed'));
        }
    };

    // ─── Assign-edit dialog (bottom table) ───────────────────────────────────

    const fetchApprovedJobsForBranch = React.useCallback(async (branchNameOrId: string) => {
        if (!token || !branchNameOrId || branchNameOrId === "All") return;
        setApprovedJobsLoading(true);
        try {
            // row.branch holds the branch name — resolve it to the branch ID
            const resolvedId =
                branches.find((b) => b.name === branchNameOrId)?.id ??
                branches.find((b) => b.id === branchNameOrId)?.id ??
                branchNameOrId;
            const response = await fetch(
                `${API_BASE_URL}/api/job-positions/status/approved?branchId=${encodeURIComponent(resolvedId)}`,
                {method: "GET", headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`}},
            );
            if (!response.ok) throw new Error(`Failed to fetch approved jobs: ${response.statusText}`);
            const data = await response.json();
            setApprovedJobOptions(data.jobs ?? data ?? []);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.approvedFetchFailed'));
            setApprovedJobOptions([]);
        } finally {
            setApprovedJobsLoading(false);
        }
    }, [token, branches, t]);

    const fetchApprovedJobsForBranchExcludeAssigned = React.useCallback(async (branchNameOrId: string) => {
        if (!token || !branchNameOrId || branchNameOrId === "All") return;
        setApprovedJobsLoading(true);
        try {
            const resolvedId =
                branches.find((b) => b.name === branchNameOrId)?.id ??
                branches.find((b) => b.id === branchNameOrId)?.id ??
                branchNameOrId;
            const response = await fetch(
                `${API_BASE_URL}/api/job-positions/status/approved?branchId=${encodeURIComponent(resolvedId)}&excludeAssignedJobs=true`,
                {method: "GET", headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`}},
            );
            if (!response.ok) throw new Error(`Failed to fetch approved jobs: ${response.statusText}`);
            const data = await response.json();
            setApprovedJobOptions(data.jobs ?? data ?? []);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.approvedFetchFailed'));
            setApprovedJobOptions([]);
        } finally {
            setApprovedJobsLoading(false);
        }
    }, [token, branches, t]);

    const handleAssignEditOpen = async (row: JobPosition) => {
        setAssignEditRow(row);
        setAssignEditJobId(row.id);
        setAssignEditActive(row.active);
        setAssignEditOpen(true);
        await fetchApprovedJobsForBranch(row.branch || selectedBranch);
    };

    const handleAssignEditClose = () => {
        setAssignEditOpen(false);
        setAssignEditRow(null);
        setApprovedJobOptions([]);
    };

    const handleAssignEditSave = async () => {
        if (!assignEditJobId || !assignEditRow) return;
        const selectedJob = approvedJobOptions.find((j) => j.id === assignEditJobId);
        if (!selectedJob) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/job-positions/update`, {
                method: "PUT",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
                body: JSON.stringify({
                    id: assignEditRow.id,
                    branchId: assignEditRow.branch || selectedBranch,
                    jobPositionId: selectedJob.id,
                    name: selectedJob.name,
                    active: assignEditActive === true,
                }),
            });
            if (!response.ok) throw new Error(`Failed to update job assignment: ${response.statusText}`);
            handleAssignEditClose();
            showSnackbar(t('hr.jobs.success.assignmentUpdated'));
            await fetchJobs(selectedBranch);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.assignmentUpdateFailed'));
        }
    };

    // ─── Add dialog (original) ────────────────────────────────────────────────

    const handleAddOpen = async (branchId?: string) => {
        const target = branchId ?? selectedBranch;
        setAddBranchId(target);
        setAddJobId("");
        setAddActive(false);
        setAddDialogOpen(true);
        await fetchApprovedJobsForBranchExcludeAssigned(target);
    };
    const handleAddClose = () => {
        setAddDialogOpen(false);
        setApprovedJobOptions([]);
    };

    const handleAddSave = async () => {
        if (!addJobId) return;
        const selectedJob = approvedJobOptions.find((j) => j.id === addJobId);
        if (!selectedJob) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/job-positions/create`, {
                method: "POST",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
                body: JSON.stringify({
                    branchId: addBranchId || selectedBranch,
                    id: selectedJob.id,
                    name: selectedJob.name,
                    active: addActive === true,
                }),
            });
            if (!response.ok) throw new Error(`Failed to add job position: ${response.statusText}`);
            handleAddClose();
            showSnackbar(t('hr.jobs.success.positionAdded'));
            await fetchJobs(selectedBranch);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.positionAddFailed'));
        }
    };

    // ─── New right card: sort / filter / pagination ───────────────────────────

    const handleCardSort = (field: SortableKey) => {
        const isAsc = cardOrderBy === field && cardOrder === "asc";
        setCardOrder(isAsc ? "desc" : "asc");
        setCardOrderBy(field);
    };

    const cardFilteredJobs = React.useMemo(() => {
        const q = cardSearchQuery.trim().toLowerCase();
        return allJobs.filter((j) => {
            if (statusFilter === "Active" && !j.active) return false;
            if (statusFilter === "Inactive" && j.active) return false;
            return !q || j.name?.toLowerCase().includes(q) || j.status?.toLowerCase().includes(q);
        });
    }, [allJobs, cardSearchQuery, statusFilter]);

    const cardSortedJobs = React.useMemo(
        () => [...cardFilteredJobs].sort(getComparator(cardOrder, cardOrderBy)),
        [cardFilteredJobs, cardOrder, cardOrderBy],
    );

    const cardPaginatedJobs = React.useMemo(
        () => cardSortedJobs.slice((cardPage - 1) * itemsPerPage, cardPage * itemsPerPage),
        [cardSortedJobs, cardPage],
    );

    React.useEffect(() => {
        setCardPage(1);
        setSelectedIds([]);
    }, [cardSearchQuery, selectedBranch, statusFilter]);

    // Keep the details panel on a visible row: fall back to the first filtered position.
    const selectedPosition = React.useMemo(
        () =>
            cardSortedJobs.find((j) => j.id === selectedPositionId) ?? cardSortedJobs[0] ?? null,
        [cardSortedJobs, selectedPositionId],
    );

    // Branch assignments of the selected position (assignments are matched to positions by name).
    const selectedPositionAssignments = React.useMemo(
        () => (selectedPosition ? jobs.filter((j) => j.name === selectedPosition.name) : []),
        [jobs, selectedPosition],
    );

    // ─── New right card: multi-select ────────────────────────────────────────

    const allPageSelected =
        cardPaginatedJobs.length > 0 && cardPaginatedJobs.every((j) => selectedIds.includes(j.id));
    const somePageSelected =
        cardPaginatedJobs.some((j) => selectedIds.includes(j.id)) && !allPageSelected;

    const handleSelectAll = (checked: boolean) =>
        setSelectedIds(checked ? cardPaginatedJobs.map((j) => j.id) : []);
    const handleSelectRow = (id: string, checked: boolean) =>
        setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((sid) => sid !== id)));

    // ─── New right card: 3-dot menu ───────────────────────────────────────────

    const handleCardMenuOpen = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
        setCardAnchorEl(event.currentTarget);
        setCardMenuRowId(rowId);
    };
    const handleCardMenuClose = () => {
        setCardAnchorEl(null);
        setCardMenuRowId(null);
    };
    const handleCardMenuAction = (action: () => void) => {
        handleCardMenuClose();
        action();
    };

    // ─── Inline add (new left card) ───────────────────────────────────────────

    const handleInlineAdd = async (): Promise<boolean> => {
        if (!inlineAddName.trim()) return false;
        setInlineAddSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/job-positions/create`, {
                method: "POST",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
                body: JSON.stringify({
                    branchId: selectedBranch !== "All" ? selectedBranch : undefined,
                    name: inlineAddName.trim(),
                    active: inlineAddActive === true,
                }),
            });
            if (!response.ok) throw new Error(`Failed to add job position: ${response.statusText}`);
            setInlineAddName("");
            setInlineAddActive(false);
            showSnackbar(t('hr.jobs.success.positionAdded'));
            await fetchJobs(selectedBranch);
            await fetchAllJobs();
            return true;
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.positionAddFailed'));
            return false;
        } finally {
            setInlineAddSaving(false);
        }
    };

    // ─── Approve / Reject ─────────────────────────────────────────────────────

    const postStatusUpdates = async (
        updates: { id: string; status: string }[],
    ): Promise<void> => {
        const response = await fetch(
            `${API_BASE_URL}/api/job-positions/status/update/parent-branch`,
            {
                method: "PUT",
                headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
                body: JSON.stringify({statusUpdates: updates}),
            },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
    };

    const refreshBothTables = async () => {
        await Promise.all([fetchJobs(selectedBranch), fetchAllJobs()]);
    };

    const handleApprove = async (id: string) => {
        try {
            await postStatusUpdates([{id, status: "APPROVED"}]);
            showSnackbar(t('hr.jobs.success.positionApproved'));
            await refreshBothTables();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.positionApproveFailed'));
        }
    };

    const handleReject = async (id: string) => {
        try {
            await postStatusUpdates([{id, status: "REJECTED"}]);
            showSnackbar(t('hr.jobs.success.positionRejected'));
            await refreshBothTables();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.positionRejectFailed'));
        }
    };

    const handleBulkApprove = async () => {
        const ids = [...selectedIds];
        try {
            await postStatusUpdates(ids.map((id) => ({id, status: "APPROVED"})));
            setSelectedIds([]);
            showSnackbar(t('hr.jobs.success.positionsApproved', {count: ids.length}));
            await refreshBothTables();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.positionsApproveFailed'));
        }
    };

    const handleBulkReject = async () => {
        const ids = [...selectedIds];
        try {
            await postStatusUpdates(ids.map((id) => ({id, status: "REJECTED"})));
            setSelectedIds([]);
            showSnackbar(t('hr.jobs.success.positionsRejected', {count: ids.length}));
            await refreshBothTables();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.positionsRejectFailed'));
        }
    };

    const handleClose = async (id: string) => {
        try {
            await postStatusUpdates([{id, status: "CLOSED"}]);
            showSnackbar(t('hr.jobs.success.positionClosed'));
            await refreshBothTables();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.positionCloseFailed'));
        }
    };

    const handleBulkClose = async () => {
        const ids = [...selectedIds];
        try {
            await postStatusUpdates(ids.map((id) => ({id, status: "CLOSED"})));
            setSelectedIds([]);
            showSnackbar(t('hr.jobs.success.positionsClosed', {count: ids.length}));
            await refreshBothTables();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('hr.jobs.error.positionsCloseFailed'));
        }
    };

    return {
        error,
        setError,
        branches,
        selectedBranch,
        handleBranchChange,

        // original table
        searchInput,
        setSearchInput,
        searchQuery,
        setSearchQuery,
        order,
        orderBy,
        handleRequestSort,
        loading,
        sortedJobs,
        paginatedJobs,
        currentPage,
        setCurrentPage,
        itemsPerPage,

        // original table 3-dot menu
        anchorEl,
        menuOpen,
        menuRowId,
        handleMenuOpen,
        handleMenuClose,
        handleMenuAction,

        // original table multi-select
        isParentRejected,
        mainSelectedIds,
        mainAllPageSelected,
        mainSomePageSelected,
        handleMainSelectAll,
        handleMainSelectRow,
        handleMainApprove,
        handleMainReject,
        handleMainBulkApprove,
        handleMainBulkReject,
        handleMainClose,
        handleMainBulkClose,

        // edit dialog
        editDialogOpen,
        editName,
        setEditName,
        editActive,
        setEditActive,
        editFormErrors,
        setEditFormErrors,
        handleEditOpen,
        handleEditClose,
        handleEditSave,

        // assign-edit dialog
        assignEditOpen,
        assignEditJobId,
        setAssignEditJobId,
        assignEditActive,
        setAssignEditActive,
        approvedJobOptions,
        approvedJobsLoading,
        handleAssignEditOpen,
        handleAssignEditClose,
        handleAssignEditSave,

        // add dialog
        addDialogOpen,
        addJobId,
        setAddJobId,
        addActive,
        setAddActive,
        handleAddOpen,
        handleAddClose,
        handleAddSave,

        // page-level (redesign)
        allJobs,
        jobs,
        statusFilter,
        setStatusFilter,
        setSelectedPositionId,
        selectedPosition,
        selectedPositionAssignments,

        // left card: inline add
        inlineAddName,
        setInlineAddName,
        inlineAddActive,
        setInlineAddActive,
        inlineAddSaving,
        handleInlineAdd,

        // right card
        allJobsLoading,
        cardSearch,
        setCardSearch,
        cardSearchQuery,
        setCardSearchQuery,
        cardOrder,
        cardOrderBy,
        handleCardSort,
        cardSortedJobs,
        cardPaginatedJobs,
        cardPage,
        setCardPage,
        selectedIds,
        allPageSelected,
        somePageSelected,
        handleSelectAll,
        handleSelectRow,
        cardAnchorEl,
        cardMenuOpen,
        cardMenuRowId,
        handleCardMenuOpen,
        handleCardMenuClose,
        handleCardMenuAction,
        handleApprove,
        handleReject,
        handleClose,
        handleBulkApprove,
        handleBulkReject,
        handleBulkClose,

        // snackbar
        snackbarOpen,
        setSnackbarOpen,
        snackbarText,
        snackbarSeverity,
    };
}
